import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  Button,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider,
  Card,
  CardContent,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress
} from '@mui/material';
import {
  Stop,
  CheckCircle,
  Error,
  Schedule,
  Language,
  CloudUpload,
  Download,
  GetApp
} from '@mui/icons-material';
import { getScrapingStatus, cancelScraping, getDriveStatus, saveScrapedContentToDrive, listDriveFolders, getDriveUploadProgress } from '../api';

const ProgressMonitor = ({ scrapingJobId, onComplete, onCancel }) => {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const [startTime, setStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isDriveConnected, setIsDriveConnected] = useState(false);
  const [driveLoading, setDriveLoading] = useState(true);
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [savingToDrive, setSavingToDrive] = useState(false);
  const [driveUploadResult, setDriveUploadResult] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });

  useEffect(() => {
    // Check Drive status when component mounts
    const checkDriveStatus = async () => {
      try {
        const driveStatus = await getDriveStatus();
        setIsDriveConnected(driveStatus.authenticated);
        
        if (driveStatus.authenticated) {
          // Load folders for selection
          const foldersResult = await listDriveFolders();
          setFolders(foldersResult.folders || []);
        }
      } catch (err) {
        console.error('Error checking Drive status:', err);
        setIsDriveConnected(false);
      } finally {
        setDriveLoading(false);
      }
    };
    
    checkDriveStatus();
  }, []);

  useEffect(() => {
    if (!scrapingJobId) return;

    setStartTime(Date.now());
    
    const pollStatus = async () => {
      try {
        const statusData = await getScrapingStatus(scrapingJobId);
        setStatus(statusData);

        if (statusData.status === 'completed') {
          onComplete(statusData);
        } else if (statusData.status === 'failed') {
          setError(statusData.error || 'Scraping failed');
        } else if (statusData.status === 'cancelled') {
          onCancel();
        } else if (statusData.status === 'running') {
          // Continue polling
          setTimeout(pollStatus, 2000);
        }
      } catch (err) {
        setError(err.message);
      }
    };

    pollStatus();
  }, [scrapingJobId, onComplete, onCancel]);

  useEffect(() => {
    if (!startTime || !status || status.status !== 'running') return;

    const timer = setInterval(() => {
      setElapsedTime(Date.now() - startTime);
    }, 1000);

    return () => clearInterval(timer);
  }, [startTime, status]);

  const handleCancel = async () => {
    try {
      await cancelScraping(scrapingJobId);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDownloadAll = () => {
    const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
    const downloadUrl = `${backendUrl}/download/job/${scrapingJobId}`;
    window.location.href = downloadUrl;
  };

  const handleSaveToDrive = async () => {
    if (!selectedFolder) {
      alert('Please select a folder first');
      return;
    }
    
    setSavingToDrive(true);
    setUploadProgress({ current: 0, total: 0 });
    
    // Start real-time progress tracking
    const startProgressTracking = () => {
      // Set initial progress based on scraping results
      const estimatedTotal = status?.result?.results?.length || 29;
      setUploadProgress({ current: 0, total: estimatedTotal, currentFile: null, status: 'uploading' });
      
      const progressInterval = setInterval(async () => {
        try {
          const progressData = await getDriveUploadProgress(scrapingJobId);
          const progress = progressData.progress;
          
          console.log('Progress update:', progress); // Debug log
          
          setUploadProgress({
            current: progress.completed,
            total: progress.total,
            currentFile: progress.current_file,
            status: progress.status
          });
          
          // Stop polling if upload is completed or failed
          if (progress.status === 'completed' || progress.status === 'failed') {
            clearInterval(progressInterval);
          }
        } catch (error) {
          console.warn('Failed to get progress:', error);
          // Don't clear interval on progress fetch error - upload might still be running
        }
      }, 1000); // Poll every second
      
      return progressInterval;
    };
    
    const progressInterval = startProgressTracking();
    
    try {
      const result = await saveScrapedContentToDrive(scrapingJobId, selectedFolder.id);
      
      // Clear progress tracking
      clearInterval(progressInterval);
      
      // Store the upload result to show in detailed results
      setDriveUploadResult(result);
      
    } catch (err) {
      // Clear progress tracking on error
      clearInterval(progressInterval);
      // Set error result
      setDriveUploadResult({
        success: false,
        error: err.message,
        uploaded_count: 0,
        failed_count: 0,
        total_files: 0,
        isTimeout: err.message.includes('timeout') || err.message.includes('Upload timeout')
      });
    } finally {
      setSavingToDrive(false);
      // Ensure progress interval is cleared
      if (progressInterval) {
        clearInterval(progressInterval);
      }
    }
  };

  const formatTime = (milliseconds) => {
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const getEstimatedTimeRemaining = () => {
    if (!status || !status.completed_pages || status.completed_pages === 0) {
      return 'Calculating...';
    }

    const avgTimePerPage = elapsedTime / status.completed_pages;
    const remainingPages = status.total_pages - status.completed_pages;
    const estimatedRemaining = avgTimePerPage * remainingPages;

    return formatTime(estimatedRemaining);
  };

  const getStatusColor = (pageStatus) => {
    switch (pageStatus) {
      case 'completed':
      case 'success':
        return 'success';
      case 'failed':
      case 'error':
        return 'error';
      case 'running':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (pageStatus) => {
    switch (pageStatus) {
      case 'completed':
      case 'success':
        return <CheckCircle color="success" />;
      case 'failed':
      case 'error':
        return <Error color="error" />;
      case 'running':
        return <Schedule color="warning" />;
      default:
        return <Language />;
    }
  };

  if (!status) {
    return (
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Initializing Scraping...
        </Typography>
        <LinearProgress />
      </Paper>
    );
  }

  const progress = status.total_pages > 0 ? (status.completed_pages / status.total_pages) * 100 : 0;

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Scraping Progress
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Progress Overview */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', textAlign: 'center' }}>
              <Box>
                <Typography color="text.secondary" gutterBottom>
                  Progress
                </Typography>
                <Typography variant="h4">
                  {Math.round(progress)}%
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', textAlign: 'center' }}>
              <Box>
                <Typography color="text.secondary" gutterBottom>
                  Completed
                </Typography>
                <Typography variant="h4">
                  {status.completed_pages || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  of {status.total_pages} pages
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', textAlign: 'center' }}>
              <Box>
                <Typography color="text.secondary" gutterBottom>
                  Time Elapsed
                </Typography>
                <Typography variant="h4">
                  {formatTime(elapsedTime)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', textAlign: 'center' }}>
              <Box>
                <Typography color="text.secondary" gutterBottom>
                  Est. Remaining
                </Typography>
                <Typography variant="h4">
                  {status.status === 'running' ? getEstimatedTimeRemaining() : '-'}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Progress Bar */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2">
            {status.current_page && `Currently scraping: ${status.current_page}`}
          </Typography>
          <Typography variant="body2">
            {status.completed_pages}/{status.total_pages}
          </Typography>
        </Box>
        <LinearProgress 
          variant="determinate" 
          value={progress} 
          sx={{ height: 8, borderRadius: 4 }}
        />
      </Box>

      {/* Status Chips */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <Chip 
          icon={<Language />}
          label={`Status: ${status.status}`} 
          color={getStatusColor(status.status)}
          variant="outlined"
        />
        {status.result && (
          <>
            <Chip 
              icon={<CheckCircle />}
              label={`${status.result.successful || 0} Successful`} 
              color="success"
              variant="outlined"
            />
            <Chip 
              icon={<Error />}
              label={`${status.result.failed || 0} Failed`} 
              color="error"
              variant="outlined"
            />
          </>
        )}
      </Box>

      {/* Control Buttons */}
      {status.status === 'running' && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Button
            variant="contained"
            color="error"
            startIcon={<Stop />}
            onClick={handleCancel}
          >
            Cancel Scraping
          </Button>
        </Box>
      )}

      {/* Results List */}
      {status.result && status.result.results && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Scraping Results
          </Typography>
          
          {/* Drive Upload Progress Notice */}
          {savingToDrive && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">
                    Uploading files to Google Drive...
                  </Typography>
                </Box>
                <Box sx={{ mt: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Progress
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {uploadProgress.total > 0 ? `${uploadProgress.current} of ${uploadProgress.total} files` : 'Starting upload...'}
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant={uploadProgress.total > 0 ? "determinate" : "indeterminate"}
                    value={uploadProgress.total > 0 ? (uploadProgress.current / uploadProgress.total) * 100 : undefined}
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                  {uploadProgress.currentFile && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Uploading: {uploadProgress.currentFile}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Alert>
          )}

          {/* Drive Upload Success Notice */}
          {driveUploadResult && driveUploadResult.success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CloudUpload />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {driveUploadResult.complete_success ? 
                      `Successfully uploaded all ${driveUploadResult.uploaded_count} files to Google Drive` :
                      `Uploaded ${driveUploadResult.uploaded_count} of ${driveUploadResult.total_files} files to Google Drive`
                    }
                  </Typography>
                  {driveUploadResult.failed_count > 0 && (
                    <Typography variant="caption" color="warning.main">
                      {driveUploadResult.failed_count} files failed to upload
                    </Typography>
                  )}
                </Box>
              </Box>
            </Alert>
          )}
          
          {/* Drive Upload Error Notice */}
          {driveUploadResult && !driveUploadResult.success && (
            <Alert 
              severity={driveUploadResult.isTimeout ? "warning" : "error"} 
              sx={{ mb: 2 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Error />
                <Typography variant="body2">
                  {driveUploadResult.isTimeout ? (
                    <>
                      Upload timeout - files may still be uploading to Google Drive. 
                      Please check your Google Drive folder to verify if the files were uploaded successfully.
                    </>
                  ) : (
                    `Failed to upload files to Google Drive: ${driveUploadResult.error}`
                  )}
                </Typography>
              </Box>
            </Alert>
          )}
          
          <Box sx={{ maxHeight: 300, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <List dense>
              {status.result.results.map((result, index) => (
                <React.Fragment key={index}>
                  <ListItem>
                    <ListItemIcon>
                      {getStatusIcon(result.success ? 'success' : 'error')}
                    </ListItemIcon>
                    
                    <ListItemText
                      primary={
                        <Box>
                          <Typography variant="body2" component="div">
                            <strong>{result.title || 'No title'}</strong>
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {result.url}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                          {result.success ? (
                            <>
                              {result.content_length && (
                                <Chip 
                                  label={`${result.content_length} chars`} 
                                  size="small" 
                                  variant="outlined"
                                />
                              )}
                              {result.drive_file_id && (
                                <Chip 
                                  icon={<CloudUpload />}
                                  label="Saved to Drive" 
                                  size="small" 
                                  color="success"
                                  variant="outlined"
                                />
                              )}
                              {result.local_file_path && (
                                <Chip 
                                  icon={<GetApp />}
                                  label="Saved locally" 
                                  size="small" 
                                  color="primary"
                                  variant="outlined"
                                />
                              )}
                            </>
                          ) : (
                            <Chip 
                              label={result.error || 'Unknown error'} 
                              size="small" 
                              color="error"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < status.result.results.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Box>
        </Box>
      )}

      {/* Completion Message */}
      {status.status === 'completed' && (
        <Alert severity="success" sx={{ mt: 2 }}>
          <Typography variant="subtitle1">
            Scraping completed successfully!
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {status.result?.successful || 0} pages scraped successfully, 
            {status.result?.failed || 0} failed. 
            Total time: {formatTime(elapsedTime)}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Download />}
              onClick={handleDownloadAll}
              size="small"
            >
              Download All Content
            </Button>
            
            {!driveLoading && isDriveConnected && (
              <>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Select Folder</InputLabel>
                  <Select
                    value={selectedFolder?.id || ''}
                    onChange={(e) => {
                      const folder = folders.find(f => f.id === e.target.value);
                      setSelectedFolder(folder);
                    }}
                    label="Select Folder"
                  >
                    {folders.map((folder) => (
                      <MenuItem key={folder.id} value={folder.id}>
                        {folder.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CloudUpload />}
                  onClick={handleSaveToDrive}
                  size="small"
                  disabled={!selectedFolder || savingToDrive}
                >
                  {savingToDrive ? 'Saving...' : 'Save to Drive'}
                </Button>
              </>
            )}
            
            <Typography variant="caption" sx={{ alignSelf: 'center', color: 'text.secondary', ml: 1 }}>
              Content is saved locally and can be downloaded as a ZIP file
              {!driveLoading && isDriveConnected && ' or saved to Google Drive'}
            </Typography>
          </Box>
        </Alert>
      )}

      {status.status === 'cancelled' && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Scraping was cancelled by user.
        </Alert>
      )}
    </Paper>
  );
};

export default ProgressMonitor;
